// src/pages/services/PassportVisa.js
import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import '../../styles/ServicePage.css';
import AnimatedBackground from '../../components/AnimatedBackground';

const PassportVisa = () => {
    const fadeIn = {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.6 }
    };

    const testimonials = [
        {
            content: "Excellent Travels handled my complex visa application for a multi-country European trip with incredible efficiency. They saved me countless hours of research and stress.",
            name: "Rajiv Patel",
            location: "Mumbai, India",
            avatar: "/images/testimonials/person13.jpg"
        },
        {
            content: "When I needed an expedited passport renewal for an unexpected business trip, the team at Excellent Travels came through for me. Their expertise made what seemed impossible happen in just a few days.",
            name: "Laura Stevens",
            location: "Toronto, Canada",
            avatar: "/images/testimonials/person14.jpg"
        }
    ];

    const faqs = [
        {
            question: "How far in advance should I apply for a visa?",
            answer: "We recommend applying for visas at least 2-3 months before your planned travel date. Some countries have longer processing times, especially during peak travel seasons. For expedited services, we can process many visa applications in as little as 1-2 weeks, though additional fees may apply."
        },
        {
            question: "Can you help with passport renewals and new applications?",
            answer: "Yes, we provide comprehensive assistance with passport applications, renewals, additional pages, name changes, and expedited processing. We'll guide you through the required documentation, review your application for accuracy, and handle submission to the appropriate authorities."
        },
        {
            question: "Do I need to visit your office in person?",
            answer: "For most services, in-person visits are not required. We can handle most applications through secure digital document submission, courier services, and virtual consultations. However, some complex cases or certain country requirements may necessitate an in-person meeting."
        },
        {
            question: "What happens if my visa application is denied?",
            answer: "If your visa application is denied, we'll provide a detailed explanation of the reasons and advise on potential next steps. These may include reapplication with additional documentation, applying for a different visa category, or appealing the decision when possible. Our success rate is high because we thoroughly review applications before submission."
        }
    ];


    // Add useEffect to ensure proper scrolling
    useEffect(() => {
        // Enable scrolling when component mounts
        document.body.style.overflow = 'auto';
        document.documentElement.style.overflow = 'auto';

        // Cleanup function to ensure scrolling is enabled when component unmounts
        return () => {
            document.body.style.overflow = 'auto';
            document.documentElement.style.overflow = 'auto';
        };
    }, []);
    return (
        <motion.div
            className="service-page"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
        >
            {/* Add animated background */}
            <AnimatedBackground type="passport-visa" />

            {/* Main content */}
            <div className="service-hero">
                <img
                    src="/Assets/image/VISA-passport.webp"
                    alt="Passport and Visa Services"
                    className="service-hero-image"
                />
                <div className="service-hero-overlay">
                    <h1 className="service-title">Passport & Visa Services</h1>
                    <p className="service-subtitle">
                        Expert assistance with passport and visa processing.
                    </p>
                </div>
            </div>

            {/* Service Logo */}
            <img
                src="https://cdn-icons-png.flaticon.com/512/201/201623.png"
                alt="Passport & Visa Logo"
                className="service-logo"
            />

            <div className="service-content">
                <motion.div
                    className="service-description"
                    {...fadeIn}
                >
                    <h2>
                        <img src="https://cdn-icons-png.flaticon.com/512/201/201623.png" alt="Passport Icon" className="section-icon" />
                        Navigate International Travel Requirements with Ease
                    </h2>
                    <p>
                        At Excellent Travels, we understand that navigating the complex world of passports and visas
                        can be overwhelming. Our dedicated team of visa and passport specialists simplifies this process,
                        ensuring your travel documents are secured efficiently and accurately.
                    </p>
                    <p>
                        Our passport and visa services include:
                    </p>
                    <ul>
                        <li>Comprehensive visa application assistance for over 200 countries</li>
                        <li>Passport application, renewal, and expedited processing</li>
                        <li>Document authentication and legalization</li>
                        <li>Personalized consultation on entry requirements</li>
                        <li>Business visa and work permit assistance</li>
                        <li>Emergency and rush processing options</li>
                    </ul>

                    <div className="feature-image-container">
                        <img
                            src="/Assets/image/documnets.jpg"
                            alt="Passport and Visa Documents"
                            className="feature-image"
                        />
                        <div className="feature-image-caption">Expert assistance with all your travel documentation needs</div>
                    </div>

                    <p>
                        With our extensive experience and established relationships with consulates and embassies worldwide,
                        we navigate the bureaucratic processes efficiently, saving you time and preventing costly mistakes.
                    </p>

                    <h2>
                        <img src="https://cdn-icons-png.flaticon.com/512/2553/2553691.png" alt="Visa Services Icon" className="section-icon" />
                        Visa Services
                    </h2>
                    <p>
                        Our comprehensive visa services cover:
                    </p>
                    <ul>
                        <li><strong>Tourist Visas:</strong> For vacation and leisure travel</li>
                        <li><strong>Business Visas:</strong> For conferences, meetings, and professional activities</li>
                        <li><strong>Work Visas:</strong> For employment opportunities abroad</li>
                        <li><strong>Student Visas:</strong> For educational pursuits internationally</li>
                        <li><strong>Transit Visas:</strong> For passing through countries en route to your destination</li>
                        <li><strong>Electronic Visas (eVisas):</strong> For countries offering digital application processes</li>
                        <li><strong>Multiple-Entry Visas:</strong> For frequent travelers to the same destination</li>
                    </ul>

                    <h2>
                        <img src="https://cdn-icons-png.flaticon.com/512/5266/5266248.png" alt="Passport Services Icon" className="section-icon" />
                        Passport Services
                    </h2>
                    <p>
                        Our passport assistance includes:
                    </p>
                    <ul>
                        <li><strong>New Passport Applications:</strong> Complete guidance for first-time applicants</li>
                        <li><strong>Passport Renewals:</strong> Streamlined process for existing passport holders</li>
                        <li><strong>Expedited Processing:</strong> Fast-track options for urgent travel needs</li>
                        <li><strong>Lost/Stolen Passport Replacement:</strong> Emergency assistance when you need it most</li>
                        <li><strong>Child Passport Applications:</strong> Specialized guidance for minors' travel documents</li>
                        <li><strong>Name Change and Passport Updates:</strong> Assistance with documentation modifications</li>
                    </ul>

                    <h2>
                        <img src="https://cdn-icons-png.flaticon.com/512/2784/2784403.png" alt="Process Icon" className="section-icon" />
                        Our Process
                    </h2>
                    <p>
                        Working with our passport and visa team is simple:
                    </p>
                    <ol>
                        <li><strong>Consultation:</strong> We assess your travel plans and document needs</li>
                        <li><strong>Documentation Guidance:</strong> We provide a detailed checklist of required materials</li>
                        <li><strong>Application Review:</strong> Our experts thoroughly check your application for accuracy</li>
                        <li><strong>Submission:</strong> We handle the submission process with the appropriate authorities</li>
                        <li><strong>Tracking:</strong> We monitor your application throughout the process</li>
                        <li><strong>Delivery:</strong> We ensure your documents are returned to you securely</li>
                    </ol>
                </motion.div>

                <motion.div
                    className="service-sidebar"
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                >
                    <img
                        src="https://cdn-icons-png.flaticon.com/512/201/201623.png"
                        alt="Passport & Visa Icon"
                        className="service-sidebar-logo"
                    />

                    <h3>Why Choose Our Passport & Visa Services</h3>
                    <ul className="service-features">
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2553/2553691.png" alt="Success Icon" />
                            </span>
                            99% success rate on visa applications
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/5266/5266248.png" alt="Regulations Icon" />
                            </span>
                            Up-to-date knowledge of changing regulations
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2784/2784389.png" alt="Service Icon" />
                            </span>
                            Personalized service and attention to detail
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2784/2784403.png" alt="Time Icon" />
                            </span>
                            Time-saving convenience
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/201/201623.png" alt="Expedited Icon" />
                            </span>
                            Expedited processing options
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2921/2921222.png" alt="Secure Icon" />
                            </span>
                            Secure document handling
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2933/2933087.png" alt="Multilingual Icon" />
                            </span>
                            Multilingual support staff
                        </li>
                    </ul>
                </motion.div>
            </div>

            <motion.div
                className="cta-section"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
            >
                <h2>Ready to Secure Your Travel Documents?</h2>
                <p>
                    Contact our passport and visa specialists today for service fees and processing timelines to ensure smooth processing for your next international journey.
                </p>
                <Link to="/contact" className="cta-button">
                    Request Visa Consultation
                    <span className="arrow-icon">→</span>
                </Link>
            </motion.div>

            <motion.div
                className="testimonials"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.6 }}
            >
                <h2>What Our Clients Say</h2>
                <div className="testimonials-grid">
                    {testimonials.map((testimonial, index) => (
                        <motion.div
                            key={index}
                            className="testimonial-card"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.7 + (index * 0.1) }}
                        >
                            <p className="testimonial-content">"{testimonial.content}"</p>
                            <div className="testimonial-author">
                                <div className="author-info">
                                    <h4>{testimonial.name}</h4>
                                    <p>{testimonial.location}</p>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </motion.div>

            <motion.div
                className="faq-section"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.8 }}
            >
                <h2>Frequently Asked Questions</h2>
                {faqs.map((faq, index) => (
                    <motion.div
                        key={index}
                        className="faq-item"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.9 + (index * 0.1) }}
                    >
                        <h3 className="faq-question">{faq.question}</h3>
                        <p className="faq-answer">{faq.answer}</p>
                    </motion.div>
                ))}
            </motion.div>
        </motion.div>
    );
};

export default PassportVisa;